import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, Split } from 'lucide-react';
import SchemaFieldRenderer from './SchemaFieldRenderer';
import styles from './ListField.module.css';

function ListField({ 
  fieldSchema, 
  value = [], 
  onChange, 
  onBlur,
  isEditing, 
  lookupData = {},
  className = ''
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const items = Array.isArray(value) ? value : [];
  const itemFields = fieldSchema.fields || {};

  // Handle item field change
  const handleItemFieldChange = (itemIndex, fieldName, newValue) => {
    const updatedItems = [...items];
    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      [fieldName]: newValue
    };
    onChange?.(updatedItems);
  };

  // Add new item
  const handleAddItem = () => {
    const newItem = {};
    // Initialize fields with default values based on type
    Object.entries(itemFields).forEach(([fieldName, fieldDef]) => {
      switch (fieldDef.type) {
        case 'string':
          newItem[fieldName] = '';
          break;
        case 'number':
          newItem[fieldName] = 0;
          break;
        case 'boolean':
          newItem[fieldName] = false;
          break;
        case 'lookup':
          newItem[fieldName] = null;
          break;
        default:
          newItem[fieldName] = null;
      }
    });
    
    // Add unique ID
    newItem.id = `new_${Date.now()}`;
    
    const updatedItems = [...items, newItem];
    onChange?.(updatedItems);
  };

  // Remove item
  const handleRemoveItem = (itemIndex) => {
    const updatedItems = items.filter((_, index) => index !== itemIndex);
    onChange?.(updatedItems);
  };

  // Split item (duplicate with half amounts)
  const handleSplitItem = (itemIndex) => {
    const itemToSplit = items[itemIndex];
    const newItem = { ...itemToSplit };
    
    // If the item has an amount field, split it in half
    if (itemToSplit.amount && typeof itemToSplit.amount === 'number') {
      const halfAmount = itemToSplit.amount / 2;
      newItem.amount = halfAmount;
      
      // Update original item
      handleItemFieldChange(itemIndex, 'amount', halfAmount);
    }
    
    // Add unique ID to new item
    newItem.id = `split_${Date.now()}`;
    
    // Add description indicator
    if (newItem.description) {
      newItem.description = `${newItem.description} - Split`;
    } else if (newItem.name) {
      newItem.name = `${newItem.name} - Split`;
    }
    
    // Insert new item right after the current one
    const updatedItems = [...items];
    updatedItems.splice(itemIndex + 1, 0, newItem);
    onChange?.(updatedItems);
  };

  // Calculate total amount if applicable
  const totalAmount = items.reduce((sum, item) => {
    const amount = parseFloat(item.amount) || 0;
    return sum + amount;
  }, 0);

  const hasAmountField = Object.keys(itemFields).includes('amount');

  // Render summary view (collapsed)
  const renderSummary = () => {
    const count = items.length;
    if (count === 0) {
      return (
        <div className={styles.summaryEmpty}>
          No line items
        </div>
      );
    }

    if (count === 1) {
      const item = items[0];
      const displayField = itemFields.name ? 'name' : 
                          itemFields.description ? 'description' : 
                          Object.keys(itemFields)[0];
      
      return (
        <div className={styles.summarySingle}>
          <span className={styles.summaryText}>
            {item[displayField] || 'Unnamed item'}
          </span>
          {hasAmountField && item.amount && (
            <span className={styles.summaryAmount}>
              ${Number(item.amount).toFixed(2)}
            </span>
          )}
        </div>
      );
    }

    return (
      <div className={styles.summaryMultiple}>
        <span className={styles.summaryCount}>
          {count} line items
        </span>
        {hasAmountField && (
          <span className={styles.summaryTotal}>
            Total: ${totalAmount.toFixed(2)}
          </span>
        )}
      </div>
    );
  };

  // Render expanded view
  const renderExpanded = () => {
    if (items.length === 0) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyMessage}>
            No line items yet
          </div>
          {isEditing && (
            <button
              onClick={handleAddItem}
              className={styles.addFirstButton}
            >
              <Plus className={styles.addIcon} />
              Add first item
            </button>
          )}
        </div>
      );
    }

    return (
      <div className={styles.itemsList}>
        <div className={styles.itemsHeader}>
          {Object.entries(itemFields).map(([fieldName, fieldDef]) => (
            <div key={fieldName} className={styles.headerCell}>
              {fieldName.replace(/_/g, ' ').toUpperCase()}
            </div>
          ))}
          {isEditing && (
            <div className={styles.headerCell}>
              Actions
            </div>
          )}
        </div>

        {items.map((item, index) => (
          <div key={item.id || index} className={styles.itemRow}>
            {Object.entries(itemFields).map(([fieldName, fieldDef]) => (
              <div key={fieldName} className={styles.itemCell}>
                <SchemaFieldRenderer
                  fieldSchema={fieldDef}
                  fieldName={fieldName}
                  value={item[fieldName]}
                  onChange={(newValue) => handleItemFieldChange(index, fieldName, newValue)}
                  onBlur={() => setEditingItem(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setEditingItem(null);
                    }
                  }}
                  isEditing={isEditing && editingItem === `${index}-${fieldName}`}
                  lookupData={lookupData}
                  className={styles.itemFieldInput}
                />
              </div>
            ))}
            
            {isEditing && (
              <div className={styles.itemActions}>
                <button
                  onClick={() => handleSplitItem(index)}
                  className={styles.actionButton}
                  title="Split this item"
                >
                  <Split className={styles.actionIcon} />
                </button>
                {items.length > 1 && (
                  <button
                    onClick={() => handleRemoveItem(index)}
                    className={styles.deleteButton}
                    title="Remove this item"
                  >
                    <Trash2 className={styles.actionIcon} />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        {isEditing && (
          <div className={styles.addItemRow}>
            <button
              onClick={handleAddItem}
              className={styles.addItemButton}
            >
              <Plus className={styles.addIcon} />
              Add line item
            </button>
          </div>
        )}

        {hasAmountField && items.length > 1 && (
          <div className={styles.totalRow}>
            <div className={styles.totalLabel}>Total:</div>
            <div className={styles.totalAmount}>
              ${totalAmount.toFixed(2)}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Read-only view
  if (!isEditing) {
    return (
      <div className={`${styles.listFieldContainer} ${className}`}>
        <div 
          className={styles.listHeader}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <ChevronDown className={styles.expandIcon} />
          ) : (
            <ChevronRight className={styles.expandIcon} />
          )}
          {renderSummary()}
        </div>
        
        {isExpanded && (
          <div className={styles.listContent}>
            {renderExpanded()}
          </div>
        )}
      </div>
    );
  }

  // Editable view (always expanded)
  return (
    <div className={`${styles.listFieldContainer} ${styles.editableContainer} ${className}`}>
      <div className={styles.editableHeader}>
        <div className={styles.editableTitle}>
          Line Items ({items.length})
        </div>
        {hasAmountField && items.length > 0 && (
          <div className={styles.editableTotal}>
            Total: ${totalAmount.toFixed(2)}
          </div>
        )}
      </div>
      
      <div className={styles.listContent}>
        {renderExpanded()}
      </div>
    </div>
  );
}

export default ListField;